import * as THREE from 'three';
import type { BoardNode } from '../../../shared/board/types';
import { isFloatingBoardNode } from '../../../shared/board/snap-rules';

export interface FloatingTileClouds {
  readonly group: THREE.Group;
  getCloudGroup(nodeId: string): THREE.Group | undefined;
  update(deltaSeconds: number): void;
  dispose(): void;
}

interface FloatingTileCloudsConfig {
  readonly nodes: readonly BoardNode[];
  readonly parent: THREE.Group;
  readonly getTileMesh: (nodeId: string) => THREE.Mesh | undefined;
}

interface CloudSprite {
  readonly group: THREE.Group;
  readonly base: THREE.Vector3;
  readonly drift: THREE.Vector3;
  readonly baseScale: number;
  readonly phase: number;
  readonly squash: THREE.Vector3;
}

interface CloudRecord {
  readonly nodeId: string;
  readonly tileMesh: THREE.Mesh;
  readonly group: THREE.Group;
  readonly sprites: CloudSprite[];
}

const CLOUDS_PER_TILE = 6;
const CLOUD_VERTICAL_OFFSET = 0.46;
const CLOUD_OFFSET = new THREE.Vector3(0, -CLOUD_VERTICAL_OFFSET, 0);
const OUTER_PUFF_MIN_RADIUS = 0.2;
const OUTER_PUFF_RADIUS_VARIANCE = 0.2;

function hashUnit(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0xffffffff;
}

function createCloudRecord(node: BoardNode, tileMesh: THREE.Mesh): CloudRecord {
  const group = new THREE.Group();
  group.name = `floating-tile-cloud:${node.id}`;
  group.userData.nodeId = node.id;

  const sprites: CloudSprite[] = [];
  for (let index = 0; index < CLOUDS_PER_TILE; index += 1) {
    const seed = `${node.id}:${index}`;
    const isCenterPuff = index === 0;
    const angle =
      (index / Math.max(1, CLOUDS_PER_TILE - 1)) * Math.PI * 2 + hashUnit(`${seed}:angle`) * 0.4;
    const radius = isCenterPuff
      ? 0
      : OUTER_PUFF_MIN_RADIUS + hashUnit(`${seed}:radius`) * OUTER_PUFF_RADIUS_VARIANCE;
    const base = new THREE.Vector3(
      Math.cos(angle) * radius,
      (hashUnit(`${seed}:height`) - 0.5) * 0.12,
      Math.sin(angle) * radius,
    );
    const baseScale =
      (isCenterPuff ? 1.28 : 0.78) + hashUnit(`${seed}:scale`) * (isCenterPuff ? 0.12 : 0.18);
    sprites.push({
      group,
      base,
      drift: new THREE.Vector3(
        Math.cos(angle + Math.PI * 0.5) * 0.026,
        0.014,
        Math.sin(angle + Math.PI * 0.5) * 0.026,
      ),
      baseScale,
      phase: hashUnit(`${seed}:phase`) * Math.PI * 2,
      squash: new THREE.Vector3(
        1.08 + hashUnit(`${seed}:wide`) * 0.24,
        0.42 + hashUnit(`${seed}:heightScale`) * 0.16,
        0.82 + hashUnit(`${seed}:deep`) * 0.2,
      ),
    });
  }

  return { nodeId: node.id, tileMesh, group, sprites };
}

export function createFloatingTileClouds(config: FloatingTileCloudsConfig): FloatingTileClouds {
  const root = new THREE.Group();
  root.name = 'floating-tile-clouds';
  config.parent.add(root);

  const records: CloudRecord[] = [];
  const recordsByNodeId = new Map<string, CloudRecord>();
  const sprites: CloudSprite[] = [];
  let time = 0;
  let disposed = false;

  for (const node of config.nodes) {
    if (node.type === 'pathing' || !isFloatingBoardNode(node)) continue;
    const tileMesh = config.getTileMesh(node.id);
    if (tileMesh == null) continue;
    const record = createCloudRecord(node, tileMesh);
    records.push(record);
    recordsByNodeId.set(node.id, record);
    sprites.push(...record.sprites);
    root.add(record.group);
  }

  const cloudGeometry = sprites.length > 0 ? new THREE.PlaneGeometry(1, 1) : null;
  const cloudMaterial =
    sprites.length > 0
      ? new THREE.ShaderMaterial({
          uniforms: {
            uTime: { value: 0 },
            uColorTop: { value: new THREE.Color(0xffffff) },
            uColorBottom: { value: new THREE.Color(0xeee9dd) },
          },
          vertexShader: `
            attribute vec3 instanceCloudOffset;
            attribute vec2 instanceCloudScale;
            attribute float instanceCloudPhase;
            varying vec2 vUv;
            varying float vPhase;

            void main() {
              vUv = uv;
              vPhase = instanceCloudPhase;
              vec2 centered = position.xy;
              vec3 cameraRight = vec3(modelViewMatrix[0][0], modelViewMatrix[1][0], modelViewMatrix[2][0]);
              vec3 cameraUp = vec3(modelViewMatrix[0][1], modelViewMatrix[1][1], modelViewMatrix[2][1]);
              vec3 worldPosition = instanceCloudOffset +
                cameraRight * centered.x * instanceCloudScale.x +
                cameraUp * centered.y * instanceCloudScale.y;
              gl_Position = projectionMatrix * viewMatrix * vec4(worldPosition, 1.0);
            }
          `,
          fragmentShader: `
            uniform float uTime;
            uniform vec3 uColorTop;
            uniform vec3 uColorBottom;
            varying vec2 vUv;
            varying float vPhase;

            float hash(vec2 p) {
              return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
            }

            float noise(vec2 p) {
              vec2 i = floor(p);
              vec2 f = fract(p);
              vec2 u = f * f * (3.0 - 2.0 * f);
              return mix(
                mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
                mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
                u.y
              );
            }

            void main() {
              vec2 p = vUv * 2.0 - 1.0;
              p.x *= 1.12;
              float d = length(p);
              float n = noise(vUv * 4.2 + vec2(uTime * 0.045 + vPhase, -uTime * 0.028));
              float edge = 0.9 + n * 0.08;
              float alpha = smoothstep(edge, edge - 0.48, d);
              alpha *= 0.42 + smoothstep(0.9, 0.0, d) * 0.3;
              if (alpha < 0.01) discard;

              float upper = smoothstep(-0.95, 0.65, p.y);
              float centerLight = smoothstep(0.95, 0.0, d);
              vec3 color = mix(uColorBottom, uColorTop, upper * 0.58 + centerLight * 0.42);
              color += vec3(0.08) * centerLight;
              gl_FragColor = vec4(color, alpha);
            }
          `,
          transparent: true,
          depthWrite: false,
          depthTest: true,
          blending: THREE.NormalBlending,
        })
      : null;
  const cloudMesh =
    cloudGeometry != null && cloudMaterial != null
      ? new THREE.InstancedMesh(cloudGeometry, cloudMaterial, sprites.length)
      : null;
  if (cloudMesh != null) {
    cloudMesh.name = 'floating-tile-cloud-puffs';
    cloudMesh.frustumCulled = false;
    cloudMesh.castShadow = false;
    cloudMesh.receiveShadow = false;
    root.add(cloudMesh);
  }

  const positionScratch = new THREE.Vector3();
  const instanceOffsets = new Float32Array(sprites.length * 3);
  const instanceScales = new Float32Array(sprites.length * 2);
  const instancePhases = new Float32Array(sprites.length);
  if (cloudGeometry != null) {
    cloudGeometry.setAttribute(
      'instanceCloudOffset',
      new THREE.InstancedBufferAttribute(instanceOffsets, 3),
    );
    cloudGeometry.setAttribute(
      'instanceCloudScale',
      new THREE.InstancedBufferAttribute(instanceScales, 2),
    );
    cloudGeometry.setAttribute(
      'instanceCloudPhase',
      new THREE.InstancedBufferAttribute(instancePhases, 1),
    );
  }

  function update(deltaSeconds: number): void {
    if (disposed) return;
    time += deltaSeconds;
    for (const record of records) {
      record.group.position.copy(record.tileMesh.position).add(CLOUD_OFFSET);
    }
    if (cloudMesh == null) return;
    for (let index = 0; index < sprites.length; index += 1) {
      const cloudSprite = sprites[index]!;
      const wave = Math.sin(time * 0.85 + cloudSprite.phase);
      const breathe = 1 + wave * 0.06;
      positionScratch.set(
        cloudSprite.group.position.x +
          cloudSprite.base.x +
          cloudSprite.drift.x * Math.sin(time * 0.42 + cloudSprite.phase),
        cloudSprite.group.position.y +
          cloudSprite.base.y +
          cloudSprite.drift.y * Math.sin(time * 0.65 + cloudSprite.phase),
        cloudSprite.group.position.z +
          cloudSprite.base.z +
          cloudSprite.drift.z * Math.cos(time * 0.42 + cloudSprite.phase),
      );
      const scale = cloudSprite.baseScale * breathe;
      instanceOffsets[index * 3] = positionScratch.x;
      instanceOffsets[index * 3 + 1] = positionScratch.y;
      instanceOffsets[index * 3 + 2] = positionScratch.z;
      instanceScales[index * 2] = scale * cloudSprite.squash.x;
      instanceScales[index * 2 + 1] = scale * cloudSprite.squash.z * 0.68;
      instancePhases[index] = cloudSprite.phase;
    }
    const offsetAttribute = cloudGeometry?.getAttribute('instanceCloudOffset');
    if (offsetAttribute != null) offsetAttribute.needsUpdate = true;
    const scaleAttribute = cloudGeometry?.getAttribute('instanceCloudScale');
    if (scaleAttribute != null) scaleAttribute.needsUpdate = true;
    const phaseAttribute = cloudGeometry?.getAttribute('instanceCloudPhase');
    if (phaseAttribute != null) phaseAttribute.needsUpdate = true;
    if (cloudMaterial instanceof THREE.ShaderMaterial) {
      cloudMaterial.uniforms.uTime!.value = time;
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    root.parent?.remove(root);
    cloudGeometry?.dispose();
    cloudMaterial?.dispose();
    for (const record of records) record.group.clear();
    root.clear();
    records.length = 0;
    recordsByNodeId.clear();
    sprites.length = 0;
  }

  update(0);

  return {
    group: root,
    getCloudGroup(nodeId: string): THREE.Group | undefined {
      return recordsByNodeId.get(nodeId)?.group;
    },
    update,
    dispose,
  };
}
